import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { StatusBannerCameraDetailsCollectorService } from '@hunting-nature/Secacam/pages/cameras/camera-details/status-banner-camera-details/status-banner-camera-details-collector.service';
import { RemoteControlActions } from '@hunting-nature/Secacam/pages/cameras/redux/remote-control.actions';
import { RemoteControlSelectors } from '@hunting-nature/Secacam/pages/cameras/redux/remote-control.reducer';
import type { BannerConfiguration } from '@hunting-nature/Secacam/shared/components/show-banner/banner-configuration';
import { isLegacyCamera } from '@hunting-nature/Secacam/shared/helper/remote-control.helper';
import { ShopService } from '@hunting-nature/Secacam/shared/services/shop.service';
import { SubscriptionService } from '@hunting-nature/Secacam/shared/services/subscription.service';
import { ToastService } from '@hunting-nature/Secacam/shared/services/toast.service';
import { ADD_CAMERA_ERROR } from '@hunting-nature/shared-core/enums/add-camera-error.enum';
import { AlwaysOnMode, GPS } from '@hunting-nature/shared-core/enums/remote-configs.enum';
import type { Contact } from '@hunting-nature/shared-core/interfaces/contact-response.interface';
import type { CameraLocations } from '@hunting-nature/shared-core/interfaces/camera-locations.interface';
import type { Camera } from '@hunting-nature/shared-core/interfaces/camera.interface';
import type { PostponedStatusBanner } from '@hunting-nature/Secacam/shared/models/postponed-status-banner';
import type { RemoteControlResponse } from '@hunting-nature/shared-core/types/remote-control-response.type';
import type { CameraViewMode } from '@hunting-nature/shared-core/utils/camera.ui.utility';
import { normalizeError } from '@hunting-nature/shared-core/utils/normalize-error';
import { NavController, ToastController } from '@ionic/angular';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, EMPTY, exhaustMap, filter, forkJoin, from, map, of, startWith, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';

import { GeneralActions } from '../../../shared/redux/general/general.actions';
import { RestService } from '../../../shared/services/rest-service';
import { SettingsService } from '../../../shared/services/settings.service';
import { StatusBannerService } from '../../../shared/services/status-banner.service';
import { StatusBannerCameraOverviewCollectorService } from '../components/status-banner-camera-overview/status-banner-camera-overview-collector.service';
import { CamerasPageService } from '../services/cameras.page.service';
import { RemoteControlService } from '../services/remote-control.service';

import { CamerasActions } from './cameras.actions';
import { CameraSelectors } from './cameras.reducer';
import { ContactsRestService } from '@hunting-nature/Secacam/shared/services/contacts/contacts.rest.service';
import { CameraModelService } from '@hunting-nature/shared-core/services/camera-model.service';
import type { CameraModelsJson } from '@hunting-nature/shared-core/interfaces/camera-models.interface';
import { getRequestPictureBlockedToastKey, getCameraImageId, isInstantModeActive } from '../../../shared/helper/camera.helper';

@Injectable()
export class CamerasEffects {
  // TODO: split effects by sub-domain (overview, details, share, gps, invite, view-mode) to avoid mega files.
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly camerasService = inject(CamerasPageService);
  private readonly remoteControlService = inject(RemoteControlService);
  private readonly restService = inject(RestService);
  private readonly toastController = inject(ToastController);
  private readonly navController = inject(NavController);
  private readonly transloco = inject(TranslocoService);
  private readonly statusBannerCameraOverviewCollectorService = inject(StatusBannerCameraOverviewCollectorService);
  private readonly statusBannerCameraDetailsCollectorService = inject(StatusBannerCameraDetailsCollectorService);
  private readonly settingsService = inject(SettingsService);
  private readonly toastService = inject(ToastService);
  private readonly contactsRestService = inject(ContactsRestService);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly shopService = inject(ShopService);
  private readonly statusBannerService = inject(StatusBannerService);
  private readonly cameraModelService = inject(CameraModelService);

  addCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.addCamera),
      exhaustMap(action => {
        return this.restService.addCamera$(action.activationCode).pipe(
          switchMap(() => [CamerasActions.addCameraSucceeded(), CamerasActions.camerasShown()]),
          catchError((error: unknown) => {
            const errorCode = this.mapResponseToError(error);
            if (errorCode != ADD_CAMERA_ERROR.CODE_USED_OR_INVALID) {
              console.error(error);
            }
            return of(CamerasActions.addCameraFailed({ error: errorCode }));
          })
        );
      })
    )
  );

  private mapResponseToError(error: unknown) {
    const status = (error as HttpErrorResponse)?.status;
    if (status === 400) {
      return ADD_CAMERA_ERROR.CODE_USED_OR_INVALID;
    }

    return ADD_CAMERA_ERROR.GENERIC;
  }

  private showErrorToast(translationKey: string): void {
    const message = this.transloco.translate(translationKey);
    from(
      this.toastController.create({
        message,
        position: 'bottom',
        duration: 2000,
        color: 'danger',
      })
    )
      .pipe(tap(toast => toast.present()))
      .subscribe();
  }

  camerasShown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.camerasShown, GeneralActions.slotManagerPageEntered),
      switchMap(() => {
        const contacts$ = this.contactsRestService.fetchAllContacts$().pipe(
          startWith([] as Array<Contact>),
          catchError((error: unknown) => {
            console.error(error);
            return of([] as Array<Contact>);
          })
        );

        const postponedStatusBanners$ = from(this.statusBannerService.getPostponedStatusBanners$()).pipe(
          startWith([] as Array<PostponedStatusBanner>),
          catchError((error: unknown) => {
            console.error(error);
            return of([] as Array<PostponedStatusBanner>);
          })
        );

        return combineLatest({
          data: this.camerasService.getCameras$(),
          contacts: contacts$,
          postponedStatusBanners: postponedStatusBanners$,
        }).pipe(
          map(({ data, contacts, postponedStatusBanners }) => {
            const orderedCameras = StatusBannerService.orderCameras([...data.ownCameras, ...data.sharedCameras, ...data.servicePlanCameras]);

            const banners = this.statusBannerCameraOverviewCollectorService.collect(orderedCameras);
            const filteredBanners = this.statusBannerService.filterAllPostponedBanners(banners, postponedStatusBanners);

            return CamerasActions.camerasShownSucceeded({
              ownCameras: data.ownCameras,
              sharedCameras: data.sharedCameras,
              servicePlanCameras: data.servicePlanCameras,
              banners: filteredBanners,
              contacts,
            });
          }),
          catchError((error: unknown) => {
            console.error(error);

            return of(
              CamerasActions.camerasShownFailed({
                error,
              })
            );
          })
        );
      })
    )
  );

  camerasPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.camerasPageEntered),
      switchMap(() => {
        // Step 1: start each load routine independently so every request can dispatch its own load and success/failure actions as it settles.
        const camerasLoaded$ = of(undefined).pipe(
          // Emit the "loading started" action.
          tap(() => this.store.dispatch(CamerasActions.camerasShown())),
          // Emit the success action as soon as this request returns.
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(CamerasActions.camerasShownFailed({ error }));
            return of(false);
          })
        );

        const cameraViewModeLoaded$ = of(undefined).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(CamerasActions.cameraViewModeLoaded())),
          switchMap(() => from(this.settingsService.getCameraViewMode$())),
          // Emit the success action as soon as this request returns.
          tap(viewMode => this.store.dispatch(CamerasActions.cameraViewModeLoadedSucceeded({ viewMode }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(CamerasActions.cameraViewModeLoadedFailed({ error }));
            return of(false);
          })
        );

        const subscriptionDetailsLoaded$ = of(undefined).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(CamerasActions.subscriptionAndCreditDetailsLoaded())),
          switchMap(() =>
            forkJoin({
              subscriptionDetails: this.subscriptionService.getSubscriptionDetails$(),
              creditDetails: this.subscriptionService.getPrepaidCreditsDetails$(),
            })
          ),
          // Emit the success action as soon as this request returns.
          tap(({ subscriptionDetails, creditDetails }) =>
            this.store.dispatch(CamerasActions.subscriptionAndCreditDetailsLoadedSucceeded({ subscriptionDetails, creditDetails }))
          ),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(CamerasActions.subscriptionAndCreditDetailsLoadedFailed({ error }));
            return of(false);
          })
        );

        // Step 2: wait until all three routines have settled, then emit the page-level terminal action based on their combined outcome.
        return forkJoin([camerasLoaded$, cameraViewModeLoaded$, subscriptionDetailsLoaded$]).pipe(
          map(results => (results.every(Boolean) ? CamerasActions.cameraPageEnteredSucceeded() : CamerasActions.cameraPageEnteredFailed())),
          catchError(() => of(CamerasActions.cameraPageEnteredFailed()))
        );
      })
    )
  );

  setCameraViewMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.setCameraViewMode),
      switchMap(({ viewMode }: { viewMode: CameraViewMode }) =>
        from(this.settingsService.setCameraViewMode$(viewMode)).pipe(
          map(() => CamerasActions.setCameraViewModeSucceeded()),
          catchError((error: unknown) => of(CamerasActions.setCameraViewModeFailed({ error })))
        )
      )
    )
  );

  showCamerasFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.camerasShownFailed),
        switchMap(() =>
          this.toastController.create({
            message: this.transloco.translate('errors.cameras.fetchFailed'),
            position: 'bottom',
            duration: 1500,
          })
        ),
        switchMap(toast => toast.present())
      ),
    { dispatch: false }
  );

  showCameraDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.showCameraDetails),
      switchMap(action =>
        forkJoin({
          camera: this.camerasService.getCameraById$(action.cameraId),
          cameraLocation: this.camerasService.getCameraGpsLocationById$(action.cameraId).pipe(
            catchError((error: unknown) => {
              console.error('Error fetching camera locations', error);
              return of(undefined);
            })
          ),
          models: this.cameraModelService.fetchCameraModels$(),
        }).pipe(
          switchMap((data: { camera: Camera; cameraLocation: CameraLocations | undefined; models: CameraModelsJson }) =>
            this.remoteControlService.fetchRemoteControlStatus$(data.camera.imei).pipe(
              map((response: RemoteControlResponse) => {
                const pendingStateAvailable = Object.keys(response?.states?.pendingState ?? {}).length > 0;
                const state = pendingStateAvailable ? response.states.pendingState : response.states.acknowledgedState;

                const gpsActive = state.gps === GPS.ON;
                const instantModeActive = isInstantModeActive(data.camera, response);

                return { ...data, gpsActive, instantModeActive, models: data.models };
              }),
              catchError((error: unknown) => {
                console.error('Failed fetching GPS status and Instant Mode status', error);

                return of({ ...data, gpsActive: false, instantModeActive: false, models: data.models });
              })
            )
          ),
          switchMap(
            (data: {
              camera: Camera;
              cameraLocation: CameraLocations | undefined;
              gpsActive: boolean;
              instantModeActive: boolean;
              models: CameraModelsJson;
            }) => {
              const banners: Array<BannerConfiguration> = this.statusBannerCameraDetailsCollectorService.collect(data.camera, data.instantModeActive);

              return from(this.statusBannerService.getPostponedStatusBanners$()).pipe(
                map(postponedStatusBanners => {
                  const filteredBanners = this.statusBannerService.filterAllPostponedBanners(banners, postponedStatusBanners);

                  return CamerasActions.showCameraDetailsSucceeded({
                    camera: data.camera,
                    cameraLocations: data.cameraLocation,
                    gpsActive: data.gpsActive,
                    instantModeActive: data.instantModeActive,
                    banners: filteredBanners,
                    models: data.models,
                  });
                })
              );
            }
          ),
          catchError((error: unknown) => {
            console.error('Failed fetching camera details', error);
            return of(CamerasActions.showCameraDetailsFailed({ error }));
          })
        )
      )
    )
  );

  showCameraDetailsFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.showCameraDetailsFailed),
        switchMap(action => {
          const error = action.error as Error;

          return this.toastController.create({
            message: `Failed fetching cameras details: ${error?.message}`,
            position: 'bottom',
            duration: 2000,
            color: 'danger',
          });
        }),
        switchMap(toast => toast.present())
      ),
    { dispatch: false }
  );

  deleteCameraAndScaleDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.deleteCameraAndScaleDownRequested),
      exhaustMap(action =>
        this.restService.deleteCamera$(action.cameraId).pipe(
          switchMap(() => {
            this.camerasService.openSubscriptionManagerPage();

            return [CamerasActions.deleteCameraSucceeded(), CamerasActions.camerasShown(), GeneralActions.goBackToCamerasFromCameraDetails()];
          }),
          catchError((error: unknown) => {
            return of(CamerasActions.deleteCameraFailed({ cameraId: action.cameraId, error: error as Error }));
          })
        )
      )
    )
  );

  deleteCameraAndKeepSlot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.deleteCameraAndKeepSlotRequested),
      switchMap(action =>
        this.restService.deleteCamera$(action.cameraId).pipe(
          switchMap(() => [CamerasActions.deleteCameraSucceeded(), CamerasActions.camerasShown(), GeneralActions.goBackToCamerasFromCameraDetails()]),
          catchError((error: unknown) => {
            return of(CamerasActions.deleteCameraFailed({ cameraId: action.cameraId, error: error as Error }));
          })
        )
      )
    )
  );

  deleteCameraFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.deleteCameraFailed),
        tap(action => {
          console.error(`Failed to delete camera ${action.cameraId}`, action.error);
          this.showErrorToast('errors.cameras.deleteFailed');
        })
      ),
    { dispatch: false }
  );

  swapCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.swapCameraRequested),
      switchMap(_action =>
        // TODO: Call appropriate API when ready
        // this.restService.swapCamera$(action.cameraId).pipe(
        of(undefined).pipe(
          switchMap(() => [CamerasActions.deleteCameraSucceeded(), CamerasActions.camerasShown(), GeneralActions.goBackToCamerasFromCameraDetails()]),
          catchError((_error: unknown) => {
            this.showErrorToast('errors.cameras.deleteFailed');
            return of();
          })
        )
      )
    )
  );

  editCameraNameEntered$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.editCameraNameEntered),
        filter(({ cameraId, isRenameEnabled }) => {
          if (!cameraId || !isRenameEnabled) {
            this.store.dispatch(GeneralActions.goBackToRemoteControlFromEditCameraName());
            return false;
          }
          return true;
        })
      ),
    { dispatch: false }
  );

  editCameraName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.editCameraName),
      switchMap(action =>
        this.camerasService.changeCameraNameById$(action.cameraId, action.cameraName).pipe(
          tap(() => this.navController.back()),
          map(() => CamerasActions.editCameraNameSucceeded({ cameraId: action.cameraId, cameraName: action.cameraName })),
          catchError((error: unknown) => {
            return of(CamerasActions.editCameraNameFailed({ cameraId: action.cameraId, error: error as HttpErrorResponse })); // @TODO: UI
          })
        )
      )
    )
  );

  editCameraNameFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.editCameraNameFailed),
        tap(() => this.showErrorToast('errors.cameras.renameFailed'))
      ),
    { dispatch: false }
  );

  loadCameraGpsLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.loadCameraGpsLocations),
      switchMap(action =>
        this.camerasService.getCameraGpsLocationById$(action.cameraId).pipe(
          map((cameraLocations: CameraLocations) => CamerasActions.loadCameraGpsLocationsSucceeded({ cameraLocations })),
          catchError((error: unknown) =>
            of(
              CamerasActions.loadCameraGpsLocationsFailed({
                cameraId: action.cameraId,
                error: error as HttpErrorResponse,
              })
            )
          )
        )
      )
    )
  );

  loadCameraGpsLocationsFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.loadCameraGpsLocationsFailed),
        tap(() => this.showErrorToast('errors.cameras.loadLocationFailed'))
      ),
    { dispatch: false }
  );

  enterCameraDetailsLoadCameraSharedWith$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.enterCameraDetailsLoadCameraShareContacts),
      withLatestFrom(this.store.select(CameraSelectors.getShareContacts)),
      filter(([_, shareContacts]) => !shareContacts || shareContacts.length === 0),
      switchMap(([action]) =>
        this.camerasService.fetchCameraShareContacts$(action.cameraId, 10, 0).pipe(
          map(({ shareContacts, totalCount }) => CamerasActions.enterCameraDetailsLoadCameraShareContactsSucceeded({ shareContacts, totalCount })),
          catchError((error: unknown) => {
            this.showErrorToast('errors.cameras.loadShareContactsFailed');
            return of(CamerasActions.enterCameraDetailsLoadCameraShareContactsFailed({ error: normalizeError(error) }));
          })
        )
      )
    )
  );

  activateCameraGps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.activateCameraGps),
      switchMap(action =>
        this.remoteControlService.applyGps$(action.cameraIdentCode, { gps: GPS.ON }).pipe(
          map(() => CamerasActions.activateCameraGpsSucceeded()),
          catchError((error: unknown) =>
            of(
              CamerasActions.activateCameraGpsFailed({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );

  reloadCameraGps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.reloadCameraGps),
      switchMap(action =>
        this.remoteControlService.reloadGps$(action.cameraIdentCode).pipe(
          map(() => CamerasActions.reloadCameraGpsSucceeded()),
          catchError((error: unknown) =>
            of(
              CamerasActions.reloadCameraGpsFailed({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );

  openGalleryForCamera$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.openGalleryFromButtonClicked, CamerasActions.openGalleryFromImageClicked),
        tap(
          action =>
            void this.navController.navigateForward([`/home/gallery/${action.cameraId}`], {
              state: {
                cameraFilter: {
                  id: action.cameraId,
                  name: action.cameraName,
                },
              },
            })
        )
      ),
    { dispatch: false }
  );

  updatePauseTransmissionState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.updatePauseTransmissionState),
      exhaustMap(action =>
        this.camerasService.setCameraPauseState$(action.pauseType, action.cameraId, action.cameraName, action.sleepUntil).pipe(
          switchMap(() => [
            CamerasActions.updatePauseTransmissionStateSucceeded({
              pauseType: action.pauseType,
              cameraId: action.cameraId,
              cameraName: action.cameraName,
              sleepUntil: action.sleepUntil,
            }),
          ]),
          catchError((error: unknown) => of(CamerasActions.updatePauseTransmissionStateFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  updatePauseTransmissionStateSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.updatePauseTransmissionStateSucceeded),
      map(() => GeneralActions.goBackToCameraDetailsFromPauseCamera())
    )
  );

  updatePauseTransmissionStateFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.updatePauseTransmissionStateFailed),
        tap(action => this.showErrorToast(`Failed to update pause transmission state: ${action.error.message}`)) // @todo UI
      ),
    { dispatch: false }
  );

  updateCameraShareContactPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.updateCameraShareContactPermissionRequested),
      exhaustMap(action =>
        this.restService.updateCameraShareContactPermission$(action.contactId, action.cameraId, action.permission).pipe(
          switchMap(() => [
            CamerasActions.updateCameraShareContactPermissionSucceeded({ contactId: action.contactId, permission: action.permission }),
            CamerasActions.openCameraShareInviteSentSheet(),
          ]),
          catchError((error: unknown) =>
            of(
              CamerasActions.updateCameraShareContactPermissionFailed({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );

  updateCameraShareContactPermissionFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.updateCameraShareContactPermissionFailed),
        tap(() => this.showErrorToast('errors.cameras.updateShareContactFailed'))
      ),
    { dispatch: false }
  );

  cameraShareInviteSentBackToContactsClicked$Navigate = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.cameraShareInviteSentBackToContactsClicked),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId, 'contacts']))
      ),
    { dispatch: false }
  );

  updateFirmware$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.updateFirmware),
      switchMap(action =>
        this.remoteControlService.updateFirmware$(action.identCode).pipe(
          map(() => CamerasActions.updateFirmwareSucceeded()),
          catchError((error: unknown) =>
            of(
              CamerasActions.updateFirmwareFailed({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );

  updateFirmwareFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.updateFirmwareFailed),
        tap(() => this.showErrorToast('cameras.details.firmwareUpdateModal.failed'))
      ),
    { dispatch: false }
  );

  deactivateAlwaysOnMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.deactivateAlwaysOnMode),
      tap(() => this.toastService.showTranslatedInfo$('cameras.details.alwaysOnModeDeactivate.toastMessage')),
      map(action => RemoteControlActions.setLocalAlwaysOn({ cameraIdentCode: action.cameraIdentCode, imei: action.imei, enabled: false }))
    )
  );

  requestPicture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.requestPicture),
      exhaustMap(action => {
        if (!action.cameraIdentCode) {
          return of(CamerasActions.requestPictureFailed({ error: new Error('Camera identification code not found') }));
        }

        return this.remoteControlService.sendInstantPic$(action.cameraIdentCode).pipe(
          map(() => CamerasActions.requestPictureSucceeded()),
          catchError((error: unknown) => of(CamerasActions.requestPictureFailed({ error: normalizeError(error) })))
        );
      })
    )
  );

  requestPictureSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.requestPictureSucceeded),
        tap(() => void this.toastService.showTranslatedTopInfo$('cameras.details.requestPicture.toastRequested'))
      ),
    { dispatch: false }
  );

  pollCameraImageAfterRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.requestPictureSucceeded),
      withLatestFrom(this.store.select(CameraSelectors.getCameraDetails)),
      switchMap(([, cameraDetails]) => {
        const cameraId = cameraDetails?.id;
        if (!cameraId) {
          return EMPTY;
        }

        return this.camerasService.watchForNewImage$(cameraId, getCameraImageId(cameraDetails)).pipe(
          map(camera => CamerasActions.cameraImageReceived({ camera })),
          takeUntil(this.actions$.pipe(ofType(CamerasActions.cameraDetailsPageLeft, CamerasActions.showCameraDetails)))
        );
      })
    )
  );

  requestPictureFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.requestPictureFailed),
        tap(() => void this.toastService.showTranslatedTopError$('errors.cameras.requestPictureFailed'))
      ),
    { dispatch: false }
  );

  requestPictureBlocked$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.requestPictureBlocked),
        map(action => getRequestPictureBlockedToastKey(action.reason)),
        filter((toastKey): toastKey is string => !!toastKey),
        tap(toastKey => void this.toastService.showTranslatedError$(toastKey))
      ),
    { dispatch: false }
  );

  activateAlwaysOnMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.activateAlwaysOnMode),
      exhaustMap(action => {
        if (!action.cameraIdentCode) {
          return of(CamerasActions.activateAlwaysOnModeFailed({ error: new Error('Camera identification code not found') }));
        }

        return this.remoteControlService.applyInstantMode$(action.cameraIdentCode, { instantMode: AlwaysOnMode.On }).pipe(
          map(() => CamerasActions.activateAlwaysOnModeSucceeded()),
          catchError((error: unknown) => of(CamerasActions.activateAlwaysOnModeFailed({ error: normalizeError(error) })))
        );
      })
    )
  );

  activateAlwaysOnModeSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.activateAlwaysOnModeSucceeded),
        tap(() => void this.toastService.showTranslatedInfo$('cameras.details.requestPicture.activation.toastActivated'))
      ),
    { dispatch: false }
  );

  activateAlwaysOnModeFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.activateAlwaysOnModeFailed),
        tap(() => void this.toastService.showTranslatedError$('errors.cameras.activateAlwaysOnModeFailed'))
      ),
    { dispatch: false }
  );

  openAddCreditsSite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.openAddCreditsSite),
        switchMap(() => this.shopService.openSubscriptionManager())
      ),
    { dispatch: false }
  );

  slotManagerPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.slotManagerPageEntered),
      tap(() => this.store.dispatch(CamerasActions.subscriptionDetailsLoaded())),
      switchMap(() =>
        this.subscriptionService.getSubscriptionDetails$().pipe(
          map(subscriptionDetails => CamerasActions.subscriptionDetailsLoadedSucceeded({ subscriptionDetails })),
          catchError((error: unknown) => of(CamerasActions.subscriptionDetailsLoadedFailed({ error })))
        )
      )
    )
  );

  regenerateCameraDetailsBanners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.goBackToCameraDetailsFromRemoteControl),
      withLatestFrom(this.store.select(CameraSelectors.getCameraDetails), this.store.select(RemoteControlSelectors.instantModeOff)),
      switchMap(([_action, camera, instantModeOff]) => {
        if (!camera) {
          return EMPTY;
        }

        const isLegacy = isLegacyCamera(camera?.model);
        const instantModeActive = !isLegacy && !instantModeOff;
        const banners: Array<BannerConfiguration> = this.statusBannerCameraDetailsCollectorService.collect(camera, instantModeActive);

        return from(this.statusBannerService.getPostponedStatusBanners$()).pipe(
          map(postponedStatusBanners => {
            const filteredBanners = this.statusBannerService.filterAllPostponedBanners(banners, postponedStatusBanners);

            return CamerasActions.refreshCameraDetailsBanners({
              banners: filteredBanners,
            });
          })
        );
      })
    )
  );
}
